import React, { useEffect, useMemo, useState } from "react";
import { ROUTES } from "../../../constants/routes-path";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    Input,
    LightMode,
    Stack,
    Tab,
    Tabs,
    TabList,
    TabPanel,
    TabPanels,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import CardHeader from "../../../components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
    checkErrors,
    isValidVehiclePlate,
    plateMask
} from "../../../utils/forms-utils";
import { SearchIcon } from "@chakra-ui/icons";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";

const QueriesVehicle = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const textColor = useColorModeValue("gray.700", "white");
    const formSpacing = "30px";
    const translationKey = "queries";
    const formTranslationKey = `${translationKey}.form`;
    const formDirection = { sm: "column", md: "row" };
    const failedQuery = location.state?.failedVehicleQuery;

    const [formData, setFormData] = useState(undefined);
    const [inputError, setInputError] = useState({});
    const [tabIndex, setTabIndex] = useState(failedQuery?.type === "chassis" ? 1 : 0);

    const ids = useMemo(() => ({
        placaVeiculo: "vehicle-plate",
        chassiVeiculo: "vehicle-chassis",
    }), []);

    const maskMap = {
        [ids.placaVeiculo]: plateMask,
    };

    const initFormData = () => ({
        [ids.placaVeiculo]: {
            value: "",
            validations: [{ required: true }],
        },
        [ids.chassiVeiculo]: {
            value: "",
            validations: [{ required: true }],
        }
    });

    const validatorMap = {
        [ids.placaVeiculo]: {
            validate: (v) => isValidVehiclePlate(v),
            errorKey: 'invalid-plate',
        },
        [ids.chassiVeiculo]: {
            validate: (v) => v,
            errorKey: 'invalid-chassis',
        },
    };

    const handleClickClearQuery = () => {
        setFormData(initFormData());
        setInputError({});
    };

    const handleVehiclePlateSearch = () => {
        navigate(`${ROUTES.transportManagementPath}${ROUTES.searches}${ROUTES.searchVehicle}${ROUTES.searchPlate}?valor=${formData[ids.placaVeiculo].value}`);
    };

    const handleVehicleChassisSearch = () => {
        navigate(`${ROUTES.transportManagementPath}${ROUTES.searches}${ROUTES.searchVehicle}${ROUTES.searchChassis}?valor=${formData[ids.chassiVeiculo].value}`);
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        const maskedValue = maskMap[id] ? maskMap[id](value) : value;

        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value: maskedValue },
        };

        setFormData(newFormData);
        const validator = validatorMap[id];
        let formValidationErrors = checkErrors({ formData: newFormData, inputError: inputError, name: id });

        if (validator) {
            if (!validator.validate(maskedValue)) {
                formValidationErrors[id] = t(`${translationKey}.messages.errors.${validator.errorKey}`);
            } else {
                delete formValidationErrors[id];
            }
        }

        setInputError(formValidationErrors);
    };

    useEffect(() => {
        const initialFormData = initFormData();

        if (failedQuery?.type === "plate") {
            initialFormData[ids.placaVeiculo].value = plateMask(failedQuery.value ?? "");
        } else if (failedQuery?.type === "chassis") {
            initialFormData[ids.chassiVeiculo].value = failedQuery.value ?? "";
        }

        setFormData(initialFormData);
    }, []);

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
            {formData && (
                <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                    <CardHeader mb="40px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${translationKey}.title.query-vehicles`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Tabs
                            index={tabIndex}
                            onChange={setTabIndex}
                        >
                            <TabList mb="1em">
                                <Tab>{t(`${translationKey}.query-strategies.plate`)}</Tab>
                                <Tab>{t(`${translationKey}.query-strategies.chassis`)}</Tab>
                            </TabList>

                            <TabPanels>
                                <TabPanel>
                                    <Stack spacing={formSpacing} direction={formDirection}>
                                        <FormControl isRequired isInvalid={inputError[ids.placaVeiculo]}>
                                            <Input
                                                fontSize="sm"
                                                id={ids.placaVeiculo}
                                                name={ids.placaVeiculo}
                                                value={formData[ids.placaVeiculo].value.toUpperCase()}
                                                onChange={handleInputChange}
                                                autoFocus={!!failedQuery && failedQuery.type === "plate"}
                                                placeholder={t(`${formTranslationKey}.fields.vehicle-plate.placeholder`)}
                                            />
                                            <FormErrorMessage>
                                                {t(inputError[ids.placaVeiculo])}
                                            </FormErrorMessage>
                                        </FormControl>

                                        <LightMode>
                                            <Button
                                                w="125px"
                                                px="10"
                                                variant="dark"
                                                fontSize="sm"
                                                isDisabled={!isValidVehiclePlate(formData[ids.placaVeiculo].value)}
                                                leftIcon={<SearchIcon />}
                                                onClick={handleVehiclePlateSearch}
                                                _hover={(!isValidVehiclePlate(formData[ids.placaVeiculo].value)) ? {} : undefined}
                                            >
                                                {t("system.actions.search")}
                                            </Button>
                                        </LightMode>

                                        <LightMode>
                                            <Button
                                                w="125px"
                                                px="10"
                                                variant="dark"
                                                fontSize="sm"
                                                isDisabled={!formData[ids.placaVeiculo].value}
                                                onClick={handleClickClearQuery}
                                                _hover={(!isValidVehiclePlate(formData[ids.placaVeiculo].value)) ? {} : undefined}
                                            >
                                                {t("system.actions.clear")}
                                            </Button>
                                        </LightMode>
                                    </Stack>
                                </TabPanel>

                                <TabPanel>
                                    <Stack spacing={formSpacing} direction={formDirection}>
                                        <FormControl isRequired isInvalid={!!inputError[ids.chassiVeiculo]}>
                                            <Input
                                                fontSize="sm"
                                                id={ids.chassiVeiculo}
                                                name={ids.chassiVeiculo}
                                                value={formData[ids.chassiVeiculo].value}
                                                maxLength="17"
                                                onChange={handleInputChange}
                                                autoFocus={!!failedQuery && failedQuery.type === "chassis"}
                                                placeholder={t(`${formTranslationKey}.fields.vehicle-chassis.placeholder`)}
                                            />
                                            <FormErrorMessage>
                                                {t(`${translationKey}.messages.errors.invalid-chassis`)}
                                            </FormErrorMessage>
                                        </FormControl>

                                        <LightMode>
                                            <Button
                                                w="125px"
                                                px="10"
                                                variant="dark"
                                                fontSize="sm"
                                                isDisabled={!formData[ids.chassiVeiculo].value}
                                                leftIcon={<SearchIcon />}
                                                onClick={handleVehicleChassisSearch}
                                                _hover={!formData[ids.placaVeiculo].value ? {} : undefined}
                                            >
                                                {t("system.actions.search")}
                                            </Button>
                                        </LightMode>

                                        <LightMode>
                                            <Button
                                                w="125px"
                                                px="10"
                                                variant="dark"
                                                fontSize="sm"
                                                isDisabled={!formData[ids.chassiVeiculo].value}
                                                onClick={handleClickClearQuery}
                                                _hover={!formData[ids.placaVeiculo].value ? {} : undefined}
                                            >
                                                {t("system.actions.clear")}
                                            </Button>
                                        </LightMode>
                                    </Stack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </CardBody>
                </Card>
            )}
        </Flex>
    );
};

export default QueriesVehicle;
